import { IInsight, Severity } from '@jumpstart/core';
import CloseIcon from '@mui/icons-material/Close';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import TextField from '@mui/material/TextField';
import { useAutocomplete } from '@mui/base/AutocompleteUnstyled';
import request, { gql } from 'graphql-request';
import React, { forwardRef, Fragment, useEffect, useRef, useState } from 'react';
import { GRAPH_CMS_API } from '../../../services/graph-cms/utils';
import ScoutErrorAlert from '../../../widgets/ScoutErrorAlert';
import ContentPreviewEvents from './ContentPreviewEvents';
import ContentPreviewLoading from './ContentPreviewLoading';
import EventFormNew from './EventFormNew';
import { IListComponent } from './types';
import { validateInsight } from './utils';

type ShimmedEvent = Pick<IInsight, 'id' | 'name' | 'content' | 'severity' | 'active'>;
export const Transition: any = forwardRef((props: any, ref: any) => <Slide direction="up" ref={ref} {...props} />);

const getEvents = async (): Promise<ShimmedEvent[]> => {
  const query = gql`
  {
    insights() {
      id
      name
      severity
      content
      active
    }
  }
  `;
  const { insights } = await request<{ insights: ShimmedEvent[] }>(GRAPH_CMS_API, query);
  return insights;
};

export interface IEventsList extends IListComponent {
}

export default function EventsList(props: IEventsList) {
  const { onSelect, selected } = props;
  const frameContainer = useRef<any>(null);
  const [events, setEvents] = useState<ShimmedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [anchorMenu, setAnchorMenu] = React.useState<{ element: HTMLElement; event: ShimmedEvent } | null>(null);

  const severityScore = (severity?: Severity) => {
    if (!severity) {
      return 0;
    }
    switch (severity) {
      case Severity.OPTIONAL:
        return 1;
      case Severity.MINOR:
        return 2;
      case Severity.MAJOR:
        return 3;
      case Severity.CRITICAL:
        return 4;
      default:
        return 0;
    }
  };

  const sortEvents = ((a: ShimmedEvent, b: ShimmedEvent) => {
    const ax = severityScore(a.severity);
    const bx = severityScore(b.severity);
    if (ax < bx) {
      return 1;
    } else if (ax > bx) {
      return -1;
    } else {
      return a.name.localeCompare(b.name);
    }
  });
  const [addingNewEvent, setAddingNewEvent] = useState<{ copy: string | null } | null>(null);

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions
  } = useAutocomplete({
    id: 'use-autocomplete-demo',
    options: events,
    getOptionLabel: (e: ShimmedEvent) => e.name,
    freeSolo: false,
    open: true,
    clearOnBlur: false
  });

  const handleSeverity = (severity?: Severity): string => {
    if (!severity) {
      return '';
    }
    switch (severity) {
      case Severity.OPTIONAL:
        return '';
      case Severity.MINOR:
        return '🥉';
      case Severity.MAJOR:
        return '🥈';
      case Severity.CRITICAL:
        return '🥇';
      default:
        return '';
    }
  };

  const handleAdd = (id: string) => {
    onSelect(id);
    reloadEvents();
    setAddingNewEvent(null);
  };

  const reloadEvents = () => {
    setLoading(true);
    getEvents()
      .then((data: ShimmedEvent[]) => {
        setEvents(data);
        setLoading(false);
      })
      .catch(setError);
  };

  useEffect(() => {
    reloadEvents();
  }, []);

  return (
    <Fragment>

      <Grid
        container
        direction="column"
        wrap="nowrap"
        justifyContent="center"
        alignItems="center"
        sx={{
          height: '100%',
          width: '100%',
          backgroundColor: (theme) => theme.palette.background.paper
        }}
        {...getRootProps()}
      >
        <ScoutErrorAlert error={error} setError={setError}/>
        <Grid container direction="row" wrap="nowrap" justifyContent="space-between" spacing={8} style={{ padding: 24 }}>

          <Grid item xs={5}>
            <Paper style={{ height: '100%', padding: 24, maxHeight: 550, overflowY: 'scroll' }}>
              <Grid container direction="column">
                <TextField
                  placeholder="Event"
                  variant="outlined"
                  fullWidth
                  autoFocus
                  sx={{
                    width: {
                      xs: 320,
                      sm: 550
                    }
                  }}
                  InputLabelProps={{
                    ...getInputLabelProps()
                  }}
                  InputProps={{
                    ...getInputProps() as any,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? <CircularProgress color="inherit" size={20}/> : null}
                      </React.Fragment>
                    )
                  }}
                  helperText={`🥇 = Critical, 🥈 = Major, 🥉 = Minor`}
                  {...getInputProps() as any}
                />
                {groupedOptions.length > 0 &&
                <List dense disablePadding {...getListboxProps()}>
                  <ListItem button onClick={() => {
                    setAddingNewEvent({ copy: null });
                  }}><ListItemText>+ New Event</ListItemText></ListItem>
                  <React.Fragment>
                    {(groupedOptions as any)
                      .sort(sortEvents)
                      .map((option: ShimmedEvent) => {
                        return {
                          option: option,
                          validation: validateInsight(option as IInsight)
                        };
                      })
                      .map((data: any, index: number) =>
                        <ListItem
                          {...getOptionProps({ option: data.option, index })}
                          key={data.option.id}
                          selected={!!selected ? data.option.id === selected : false}
                        >
                          <ListItemButton
                            onClick={() => {
                              onSelect(data.option.id);
                            }}
                          >

                          <ListItemText
                            id={data.option.id}
                            primary={`${data.option.name} ${handleSeverity(data.option.severity)}`}
                            primaryTypographyProps={{ style: { 'textDecoration': data.option.active ? 'none' : 'line-through' } }}
                            secondary={data.option.id}
                            secondaryTypographyProps={{ style: { fontSize: 10 } }}
                          />
                          <ListItemSecondaryAction>
                            {
                              [
                                {
                                  icon: '⚠️',
                                  severity: 'warning',
                                  amount: data.validation.numWarnings
                                },
                                {
                                  icon: '❗',
                                  severity: 'error',
                                  amount: data.validation.numErrors
                                }

                              ]
                                .filter(({ amount }) => amount > 0)
                                .map(({ icon, severity, amount }) =>
                                  <Button
                                    disabled
                                    style={{
                                      width: 32,
                                      minWidth: 32
                                    }}
                                  >
                                    {icon}{amount}
                                  </Button>
                                )
                            }
                            <IconButton
                              onClick={(e) => setAnchorMenu({ element: e.currentTarget, event: data.option })}
                              size="small"
                            >
                              <MoreVertIcon/>
                            </IconButton>
                          </ListItemSecondaryAction>
                          </ListItemButton>
                        </ListItem>
                      )}
                  </React.Fragment>
                </List>
                }
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={2}>
            <Grid
              container
              direction="column"
              alignItems="center"
              justifyContent="space-around"
              style={{ height: '100%' }}
            >
              {[1, 2, 3].map((i) =>
                <Grid item key={`reload-${i}`}>
                  <DoubleArrowIcon fontSize="large"/>
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid item ref={frameContainer} xs={5}>
            <Paper style={{ height: '100%' }}>
              {frameContainer.current && (loading ? <ContentPreviewLoading
                width={frameContainer.current.offsetWidth}
                height={frameContainer.current.offsetHeight}
              /> : <ContentPreviewEvents
                width={frameContainer.current.offsetWidth}
                height={frameContainer.current.offsetHeight}
              />)}
            </Paper>
          </Grid>
        </Grid>
        {loading ? <CircularProgress size={16}/> : null}

      </Grid>

      <Dialog
        PaperProps={{ style: { height: '100%' } }}
        fullScreen
        TransitionComponent={Transition}
        open={!!addingNewEvent}
      >
        <DialogActions>
          <IconButton onClick={() => {
            setAddingNewEvent(null);
          }}><CloseIcon/></IconButton>
        </DialogActions>
        <Grid container direction="column" justifyContent="center" alignItems="center"
              sx={{
                padding: 48/4,
                width: '100%',
                '& > *': {
                  maxWidth: '550px'
                }
              }}
        >
          <EventFormNew
            onError={(err: Error) => {
              console.error(err);
              setError(err);
              setAddingNewEvent(null);
            }}
            copy={addingNewEvent ? addingNewEvent.copy : null}
            onAdded={handleAdd}
          />
        </Grid>
      </Dialog>

      <Menu
        id="event-menu"
        anchorEl={anchorMenu && anchorMenu.element}
        open={!!(anchorMenu && anchorMenu.element)}
        onClose={() => setAnchorMenu(null)}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
      >
        <MenuItem onClick={() => setAddingNewEvent({ copy: anchorMenu ? anchorMenu.event.id : null })}>Copy</MenuItem>
      </Menu>
    </Fragment>
  );
}
